<template>
  <v-card
    :loading="loading"
    class="pa-0"
  >
    <v-card-title>
      Needs Generation
      <v-spacer />
      <v-btn
        v-if="selected.length"
        color="mcsprimary"
        :loading="bulkGenerating"
        @click="generateSelected"
      >
        Create &amp; Generate Selected ({{ selectableSelected.length }})
      </v-btn>
      <v-btn
        icon
        title="Settings"
        class="ml-2"
        @click="$refs.settings.open()"
      >
        <v-icon>settings</v-icon>
      </v-btn>
      <v-btn
        outlined
        class="ml-2"
        @click="loadData"
      >
        Refresh
      </v-btn>
    </v-card-title>
    <v-card-text class="pa-0">
      <v-data-table
        v-model="selected"
        :items="candidates"
        :headers="headers"
        item-key="_key"
        show-select
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
        :loading="loading"
      >
        <template #[`item.actions`]="{ item }">
          <v-btn
            v-if="item.canGenerate"
            color="mcsprimary"
            :loading="generatingKey === rowKey(item)"
            :disabled="!!generatingKey"
            @click="generate(item)"
          >
            Create &amp; Generate
          </v-btn>
          <!-- A row short of templates is fixed in the Template Designer, which owns the batch pipeline.
               The server picks the workspace the anchor belongs to (Category/Make → its category,
               For/Idea → its topic or industry) and hands it over as templateDesignerUrl. -->
          <div v-else>
            <div class="text--secondary font-italic mb-1">
              {{ item.reason }}
            </div>
            <v-btn
              v-if="item.categoryBreakdown && item.categoryBreakdown.length"
              small
              outlined
              class="mr-1"
              title="Show this row's template count per category"
              @click="showDetails(item)"
            >
              Details
            </v-btn>
            <v-btn
              v-if="item.templateDesignerUrl"
              small
              outlined
              color="mcsprimary"
              :href="item.templateDesignerUrl"
              target="_blank"
              rel="noopener"
              title="Open this subject in the Template Designer to generate templates"
            >
              <v-icon
                left
                small
              >
                mdi-open-in-new
              </v-icon>
              Generate templates
            </v-btn>
          </div>
        </template>
        <template #no-data>
          <div class="pa-4 text--secondary">
            No Category, Make, For, or Idea pages need to be created.
          </div>
        </template>
      </v-data-table>
    </v-card-text>
    <!-- Per-category breakdown behind the row's reason. The server only sends categories the anchor
         actually has templates in, so this lists what exists, not every category in the catalog. -->
    <v-dialog
      v-model="detailsOpen"
      max-width="520"
      scrollable
    >
      <v-card v-if="detailsItem">
        <v-card-title class="subtitle-1">
          {{ detailsItem.typeName }}: {{ detailsItem.displayName }}
        </v-card-title>
        <v-card-subtitle class="text--secondary">
          {{ detailsItem.reason || 'Ready to generate' }}
        </v-card-subtitle>
        <v-card-text style="max-height: 60vh;">
          <v-simple-table dense>
            <thead>
              <tr>
                <th class="text-left">
                  Category
                </th>
                <th class="text-right">
                  Templates
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in detailsItem.categoryBreakdown"
                :key="row.categoryId"
              >
                <td>
                  {{ row.name }}
                  <v-icon
                    v-if="row.qualifies"
                    small
                    color="success"
                    title="Meets the per-category minimum"
                  >
                    mdi-check
                  </v-icon>
                </td>
                <td class="text-right">
                  {{ row.templateCount }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="detailsOpen = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <NeedsGenerationSettings
      ref="settings"
      @saved="loadData"
    />
  </v-card>
</template>

<script>
import NeedsGenerationSettings from '@/components/content/pages/needsgeneration/Settings';
export default {
  name: 'NeedsGeneration',
  components: { NeedsGenerationSettings },
  data() {
    return {
      loading: false,
      generatingKey: null,
      bulkGenerating: false,
      selected: [],
      candidates: [],
      detailsOpen: false,
      detailsItem: null,
      headers: [
        { text: 'Type', value: 'typeName' },
        { text: 'Name', value: 'displayName' },
        { text: 'Categories', value: 'totalCategoryCount' },
        { text: 'Templates', value: 'templateCount' },
        { text: '', value: 'actions', sortable: false }
      ]
    };
  },
  computed: {
    // Only rows that can actually be generated count toward a bulk run; a selected
    // not-yet-ready row is ignored (the server skips it too).
    selectableSelected() {
      return this.selected.filter((x) => x.canGenerate);
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    showDetails(item) {
      this.detailsItem = item;
      this.detailsOpen = true;
    },
    rowKey(item) {
      return `${item.contentPageTypeId}-${item.categoryId || 0}-${item.industryId || 0}-${item.topicId || 0}-${item.designGroupId || 0}`;
    },
    toPayload(item) {
      return {
        contentPageTypeId: item.contentPageTypeId,
        categoryId: item.categoryId,
        industryId: item.industryId,
        topicId: item.topicId,
        designGroupId: item.designGroupId,
        name: item.name,
        namePlural: item.namePlural,
        urlSegment: item.urlSegment
      };
    },
    loadData() {
      this.loading = true;
      this.selected = [];
      this.$store
        .dispatch('content/getNeedsGenerationPages')
        .then((data) => {
          // v-data-table selection needs a stable per-row key; the candidates have no id.
          this.candidates = (data || []).map((x) => ({ ...x, _key: this.rowKey(x) }));
        })
        .catch((error) => {
          this.logError(error, 'Failed loading needs-generation pages');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    generate(item) {
      this.generatingKey = this.rowKey(item);
      this.$store
        .dispatch('content/addPage', this.toPayload(item))
        .then(() => {
          this.$root.$snackbarSuccess(`Generating "${item.name}"`);
          // The new (unpublished) page now exists — refresh this list and the content-pages list.
          this.$bus.$emit(this.$enums.EVENT_BUS_EVENTS.PAGE_ADD_SAVED);
        })
        .catch((error) => {
          this.logError(error, 'Failed generating page');
        })
        .finally(() => {
          this.generatingKey = null;
        });
    },
    generateSelected() {
      const payloads = this.selectableSelected.map((x) => this.toPayload(x));
      if (!payloads.length) {
        return;
      }
      this.bulkGenerating = true;
      this.$store
        .dispatch('content/addPages', payloads)
        .then(() => {
          this.$root.$snackbarSuccess(`Generating ${payloads.length} page(s)`);
          this.$bus.$emit(this.$enums.EVENT_BUS_EVENTS.PAGE_ADD_SAVED);
        })
        .catch((error) => {
          this.logError(error, 'Failed generating selected pages');
        })
        .finally(() => {
          this.bulkGenerating = false;
        });
    }
  }
};
</script>
